import { useEffect, useState } from 'react';
import api from '../utils/api';
import { RefreshCw, RotateCcw, X, Eye, Package } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';
import StlViewer from '../components/StlViewer';
import LottieAnim from '../components/LottieAnim';

const STATUS_LABEL = {
  pending: '대기', stl_ready: 'STL 확인', processing: '스케일링',
  queued: '큐 대기', printing: '출력 중', completed: '완료', failed: '실패', cancelled: '취소',
};
const STATUS_COLOR = {
  pending: 'bg-gray-700 text-gray-400',
  stl_ready: 'bg-blue-500/20 text-blue-400',
  processing: 'bg-purple-500/20 text-purple-400',
  queued: 'bg-yellow-500/20 text-yellow-400',
  printing: 'bg-brand/20 text-brand',
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-700 text-gray-500',
};

export default function JobsPage() {
  const [data, setData] = useState({ jobs: [], total: 0 });
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [previewStlId, setPreviewStlId] = useState(null); // 3D 프리뷰 STL ID

  const load = () => {
    const params = new URLSearchParams({ page, limit: 50 });
    if (date) params.append('date', date);
    if (status) params.append('status', status);
    api.get(`/jobs?${params}`).then(r => setData(r.data));
  };
  useEffect(() => { load(); }, [date, status, page]);

  const retry = async id => {
    await api.post(`/jobs/${id}/retry`);
    load();
  };
  const cancel = async id => {
    if (!confirm('취소하시겠습니까?')) return;
    await api.post(`/jobs/${id}/cancel`);
    load();
  };

  return (
    <div>
      <PageHeader icon={Package} title="생산 현황">
        <button onClick={load} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
          <RefreshCw size={16} />
        </button>
      </PageHeader>

      {/* 필터 */}
      <div className="flex gap-3 mb-5">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand">
          <option value="">전체 상태</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-gray-500 text-sm self-center">총 {data.total}건</span>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs border-b border-gray-800 whitespace-nowrap">
                <th className="text-left px-4 py-3">잡 ID</th>
                <th className="text-left px-4 py-3">주문번호</th>
                <th className="text-left px-4 py-3">수령인</th>
                <th className="text-left px-4 py-3">상품</th>
                <th className="text-left px-4 py-3">사이즈코드</th>
                <th className="text-left px-4 py-3">프린터</th>
                <th className="text-left px-4 py-3">상태</th>
                <th className="text-left px-4 py-3">시작</th>
                <th className="text-left px-4 py-3">완료예정</th>
                <th className="text-right px-4 py-3">액션</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map(j => (
                <tr key={j.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 whitespace-nowrap">
                  <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">#{j.id}</td>
                  <td className="px-4 py-2.5 text-gray-300">{j.xinvanaOrderNumber || '-'}</td>
                  <td className="px-4 py-2.5 text-gray-300">{j.recipientName || '-'}</td>
                  <td className="px-4 py-2.5 text-white max-w-[120px] truncate">{j.productName || `#${j.productId}`}</td>
                  <td className="px-4 py-2.5 text-gray-400 font-mono text-xs">L:{j.leftSizeCode || '-'} / R:{j.rightSizeCode || '-'}</td>
                  <td className="px-4 py-2.5 text-gray-400">{j.printer?.name || '-'}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[j.status]}`}>
                      {STATUS_LABEL[j.status]}
                    </span>
                    {j.errorMessage && (
                      <p className="text-red-400 text-xs mt-0.5 max-w-xs truncate">{j.errorMessage}</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {j.printStartedAt ? dayjs(j.printStartedAt).format('HH:mm') : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {j.estimatedEndAt ? dayjs(j.estimatedEndAt).format('HH:mm') : '-'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {j.productStlId && (
                      <button
                        onClick={() => setPreviewStlId(j.productStlId)}
                        className="p-1.5 text-gray-500 hover:text-brand rounded hover:bg-gray-700 mr-1"
                        title="3D 프리뷰"
                      >
                        <Eye size={13} />
                      </button>
                    )}
                    {j.status === 'failed' && (
                      <button onClick={() => retry(j.id)} className="p-1.5 text-gray-500 hover:text-green-400 rounded hover:bg-gray-700 mr-1">
                        <RotateCcw size={13} />
                      </button>
                    )}
                    {!['completed', 'cancelled'].includes(j.status) && (
                      <button onClick={() => cancel(j.id)} className="p-1.5 text-gray-500 hover:text-red-400 rounded hover:bg-gray-700">
                        <X size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {data.jobs.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <LottieAnim type="empty" size={90} />
                      <span className="text-gray-600 text-sm mt-1">잡 없음</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {previewStlId && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewStlId(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 w-full max-w-3xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-white font-bold">3D 프리뷰</h2>
              <button onClick={() => setPreviewStlId(null)} className="p-2 text-gray-500 hover:text-white rounded-lg hover:bg-gray-800">
                <X size={18} />
              </button>
            </div>
            <StlViewer url={`/api/stl/${previewStlId}/file`} height={480} />
          </div>
        </div>
      )}
    </div>
  );
}
